import { useNavigate } from "react-router-dom";
import {
    BriefcaseIcon,
    ChartBarIcon,
    ChevronRightIcon,
    DocumentReportIcon,
    UserCircleIcon,
    ViewGridAddIcon,
} from "@heroicons/react/solid";

import { BUTTON_VARIANT_TYPES, COLOUR_INTENSITIES } from "../../../../../../utils/constants";

import HorizontalBarChart from "../../../../../SharedComponents/Charts/HorizontalBarChart";
import Scale from "./Scale";
import SubScale from "./SubScale";
import Button from "../../../../../SharedComponents/Button";
import BasicMotion from "../../../../../SharedComponents/Motion/BasicMotion";

function CareeringAhead(props) {
    const { score, categories, getCategoryData, getBandDescription, isNewFormat, userName, showMore, handleShowMore } =
        props;

    const navigate = useNavigate();

    return (
        <>
            {score?.reportDescription &&
                (!Array.isArray(score?.reportDescription) ? (
                    <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                        <div className="text-primary-700 text-xl font-semibold flex justify-between">
                            {score?.reportName}

                            <DocumentReportIcon className="h-6 w-6" />
                        </div>

                        <div className="bg-blue-gray-50 rounded-xl p-4 shadow-sm my-3">
                            <div className="text-primary-600 text-lg font-semibold mb-2">Description</div>

                            <p className="text-blue-gray-500">{score?.reportDescription}</p>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                        <div className="text-primary-700 text-xl font-semibold flex justify-between items-center">
                            {score?.reportName}

                            <DocumentReportIcon className="h-6 w-6" />
                        </div>

                        <div className="bg-blue-gray-50 rounded-xl p-4 shadow-sm my-3">
                            <div className="text-primary-600 text-lg font-semibold mb-2">Description</div>

                            <div className="text-blue-gray-500 flex flex-col space-y-4">
                                {score?.reportDescription.map((r, index) => (
                                    <div key={index}>
                                        <p className="text-blue-gray-500">{r.description}</p>

                                        <ul className="list-disc my-2">
                                            {r.bullets &&
                                                r.bullets.map((bullet, index) => (
                                                    <li className="text-blue-gray-500 ml-4" key={index}>
                                                        {bullet}
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}

            {score?.profile && (
                <div className="bg-white rounded-xl p-4 shadow-sm my-3 mb-6">
                    <div className="text-primary-700 text-xl font-semibold flex justify-between items-center">
                        Your Profile
                        <UserCircleIcon className="h-6 w-6" />
                    </div>

                    <div className="bg-blue-gray-50 rounded-xl p-4 shadow-sm my-3 mb-6">
                        <div className="text-primary-600 text-lg font-semibold mb-2">{score.profile}</div>
                        <p className="text-blue-gray-500">
                            {score?.profileInterpretation &&
                                (!Array.isArray(score?.profileInterpretation)
                                    ? score?.profileInterpretation
                                    : score?.profileInterpretation.map((interpretation, index) => (
                                          <ul className="list-disc" key={index}>
                                              <li className="text-blue-gray-500 ml-4">{interpretation}</li>
                                          </ul>
                                      )))}
                        </p>
                    </div>

                    <div className="bg-blue-gray-50 rounded-xl p-4 shadow-sm my-3">
                        <div className="text-primary-600 text-lg font-semibold mb-2">Recommendations</div>
                        <ul className="list-disc">
                            {score.profileRecommendation.map((recommendation, index) => {
                                if (recommendation.includes("<br>")) {
                                    const subsetsRecommendation = recommendation.split("<br>");

                                    return subsetsRecommendation.map((sub, index) =>
                                        index === 0 ? (
                                            <li className="text-blue-gray-500 ml-4" key={index}>
                                                {sub}
                                            </li>
                                        ) : (
                                            <p className="text-blue-gray-500 ml-4" key={index}>
                                                {sub}
                                            </p>
                                        )
                                    );
                                } else {
                                    return (
                                        <li className="text-blue-gray-500 ml-4" key={index}>
                                            {recommendation}
                                        </li>
                                    );
                                }
                            })}
                        </ul>
                    </div>
                </div>
            )}

            <div className="text-xl text-primary-700 my-3 font-semibold flex flex-row items-center">
                <ChartBarIcon className="h-6 w-5 mr-1.5" />
                Graphical Summary
            </div>

            <div className="bg-white p-4 rounded-xl mb-6 shadow-sm md:hidden">
                <div className="space-y-3">
                    <p className="text-lg font-medium text-blue-700">Legend</p>
                    <div className="grid grid-cols-2 gap-3">
                        {categories &&
                            categories.map((category, index) => (
                                <div className="flex flex-row space-x-2 items-center" key={index}>
                                    {getCategoryData(category, index, "icon")}
                                    <p className={"text-md " + getCategoryData(category, index, "text")}>{category}</p>
                                </div>
                            ))}
                    </div>
                </div>
            </div>

            {score &&
                categories &&
                categories.map((category, index) => (
                    <div className={"bg-white shadow-sm p-4 rounded-xl mb-6"} key={index}>
                        <div className="flex flex-col space-y-3">
                            <div className="flex justify-between items-center">
                                <p className={"text-lg font-semibold " + getCategoryData(category, index, "text")}>
                                    {category}
                                </p>

                                {getCategoryData(category, index, "icon")}
                            </div>

                            {score?.categoriesDescriptions &&
                                score?.categoriesDescriptions.map((categoryDescription, index) => {
                                    if (categoryDescription.name === category && categoryDescription.description1) {
                                        return (
                                            <p className="text-blue-gray-500 pb-2" key={index}>
                                                {categoryDescription.description1}
                                            </p>
                                        );
                                    }
                                    return null;
                                })}

                            {isNewFormat === false ? (
                                <HorizontalBarChart
                                    data={score
                                        .filter((scale) => scale.category === category)
                                        .map((scale) => {
                                            return { name: scale.name, score: scale.percentile };
                                        })}
                                    color={getCategoryData(category, index, "colour")}
                                />
                            ) : (
                                <HorizontalBarChart
                                    data={score?.reportFields
                                        .filter((scale) => scale.category === category)
                                        .map((scale) => {
                                            return { name: scale.name, score: scale.percentile };
                                        })}
                                    color={getCategoryData(category, index, "colour")}
                                />
                            )}
                        </div>
                    </div>
                ))}

            <div className="text-xl text-primary-700 my-3 font-semibold flex flex-row items-center">
                <ViewGridAddIcon className="h-6 w-5 mr-1.5" />
                Detailed Interpretation
            </div>

            <div className="grid grid-flow-row gap-8 mt-4">
                <div className="grid gap-4">
                    {isNewFormat === false
                        ? categories &&
                          score &&
                          categories
                              .map((category) => score.filter((scale) => scale.category === category))
                              .map((scales, index) => {
                                  const totalScale = scales.filter((scale) =>
                                      scale.name.toLowerCase().includes("total score")
                                  );
                                  const total = totalScale.length === 1 && (
                                      <div className={"bg-white shadow-sm p-4 rounded-xl mb-6"} key={index}>
                                          <Scale
                                              name={totalScale[0].name}
                                              percentile={totalScale[0].percentile}
                                              description={totalScale[0].description}
                                              band_description={<span>{totalScale[0].band_description}</span>}
                                              colour={getCategoryData(
                                                  totalScale[0].category,
                                                  totalScale[0].categoryIndex,
                                                  "colour"
                                              )}
                                              colourPalette={COLOUR_INTENSITIES}
                                              scaleIndex={totalScale[0].fieldIndex}
                                          />
                                      </div>
                                  );
                                  return (
                                      <div key={index} className={"bg-blue-gray-100 p-4 rounded-xl shadow-sm"}>
                                          <div
                                              className={
                                                  "transition transition-color duration-4000 ease-in-out font-semibold text-lg mb-2 text-" +
                                                  getCategoryData(
                                                      scales[0].category,
                                                      scales[0].categoryIndex,
                                                      "colour"
                                                  ) +
                                                  "-700"
                                              }
                                          >
                                              <h2>{scales[0].category}</h2>
                                          </div>
                                          {total}

                                          {scales
                                              .filter((scale) => !scale.name.toLowerCase().includes("total score"))
                                              .map((scale, index) => (
                                                  <div className={"bg-white shadow-sm p-4 rounded-xl mb-6"} key={index}>
                                                      <SubScale
                                                          userName={userName}
                                                          name={scale.name}
                                                          percentile={scale.percentile}
                                                          lowAdjective={"Low"}
                                                          highAdjective={"High"}
                                                          description={scale.description}
                                                          band_description={
                                                              scale.band_description
                                                                  ? scale.band_description
                                                                  : getBandDescription(scale.percentile, scale.name)
                                                          }
                                                          colour={getCategoryData(
                                                              scale.category,
                                                              scale.categoryIndex,
                                                              "colour"
                                                          )}
                                                          colourPalette={COLOUR_INTENSITIES}
                                                          scaleIndex={scale.fieldIndex}
                                                      />
                                                  </div>
                                              ))}
                                      </div>
                                  );
                              })
                        : categories &&
                          score &&
                          categories
                              .map((category) => score.reportFields.filter((scale) => scale.category === category))
                              .map((scales, index) => {
                                  const totalScale = scales.filter((scale) =>
                                      scale.name.toLowerCase().includes("total score")
                                  );
                                  const total = totalScale.length === 1 && (
                                      <div className={"bg-white shadow-sm p-4 rounded-xl mb-6"} key={index}>
                                          <Scale
                                              name={totalScale[0].name}
                                              percentile={totalScale[0].percentile}
                                              description={totalScale[0].description}
                                              band_description={<span>{totalScale[0].band_description}</span>}
                                              colour={getCategoryData(
                                                  totalScale[0].category,
                                                  totalScale[0].categoryIndex,
                                                  "colour"
                                              )}
                                              colourPalette={COLOUR_INTENSITIES}
                                              scaleIndex={totalScale[0].fieldIndex}
                                          />
                                      </div>
                                  );
                                  return (
                                      <div key={index} className={"bg-blue-gray-100 p-4 rounded-xl shadow-sm"}>
                                          <div
                                              className={
                                                  "transition transition-color duration-4000 ease-in-out font-semibold text-lg mb-2 text-" +
                                                  getCategoryData(
                                                      scales[0].category,
                                                      scales[0].categoryIndex,
                                                      "colour"
                                                  ) +
                                                  "-700"
                                              }
                                          >
                                              <h2>{scales[0].category}</h2>
                                          </div>

                                          {score?.categoriesDescriptions &&
                                              score?.categoriesDescriptions.map((categoryDescription, index) => {
                                                  if (
                                                      categoryDescription.name === scales[0].category &&
                                                      categoryDescription.description2
                                                  ) {
                                                      return (
                                                          <p className="text-blue-gray-500 pb-3" key={index}>
                                                              {categoryDescription.description2}
                                                          </p>
                                                      );
                                                  }
                                                  return null;
                                              })}

                                          {total}

                                          {scales
                                              .filter((scale) => !scale.name.toLowerCase().includes("total score"))
                                              .map((scale, index) => (
                                                  <div className={"bg-white shadow-sm p-4 rounded-xl mb-6"} key={index}>
                                                      <SubScale
                                                          userName={userName}
                                                          name={scale.name}
                                                          percentile={scale.percentile}
                                                          lowAdjective={"Low"}
                                                          highAdjective={"High"}
                                                          description={scale.description}
                                                          band_description={
                                                              scale.band_description
                                                                  ? scale.band_description
                                                                  : getBandDescription(scale.percentile, scale.name)
                                                          }
                                                          colour={getCategoryData(
                                                              scale.category,
                                                              scale.categoryIndex,
                                                              "colour"
                                                          )}
                                                          colourPalette={COLOUR_INTENSITIES}
                                                          scaleIndex={scale.fieldIndex}
                                                      />
                                                  </div>
                                              ))}
                                      </div>
                                  );
                              })}
                </div>
            </div>

            {score?.recommendation && (
                <>
                    <div className="text-xl text-primary-700 mt-6 font-semibold flex flex-row items-center">
                        <BriefcaseIcon className="h-6 w-5 mr-1.5" />
                        Recommendations
                    </div>

                    <div className="bg-blue-gray-100 rounded-xl p-4 shadow-sm my-3 mb-6">
                        <div className="bg-white rounded-xl p-4 shadow-sm my-3 mb-6">
                            <p className="text-blue-gray-500">{score?.recommendation?.text}</p>

                            <div className="overflow-x-auto relative m-3 border-2 border-slate-50">
                                <table className="border-separate w-full text-left text-sm text-blue-gray-500 rounded-xl">
                                    <tbody>
                                        {score?.recommendation?.table.map((t, index) => (
                                            <tr key={index}>
                                                <th
                                                    scope="col"
                                                    className="w-fit text-md text-blue-gray-600 bg-blue-gray-100 uppercase w-44 py-3 px-6"
                                                >
                                                    {t.jobZone}
                                                </th>
                                                <td className="py-3 px-6">{t.description}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="overflow-x-auto relative overflow-hidden text-sm shadow-sm rounded-xl">
                            <table className="table-auto w-full text-left text-blue-gray-500 bg-blue-gray-50 rounded-xl overflow-hidden">
                                <thead className="text-blue-gray-600 uppercase bg-blue-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3 px-6">
                                            Job Title
                                        </th>
                                        <th scope="col" className="py-3 px-6">
                                            Job Interest Areas
                                        </th>
                                        <th scope="col" className="py-3 px-6">
                                            Job Zone
                                        </th>
                                        <th scope="col" className="py-3 px-6">
                                            Job Details
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {score?.recommendedJobs.map((recommendedJob, index) => (
                                        <tr className="bg-white" key={index}>
                                            {recommendedJob?.jobCode ? (
                                                <td
                                                    className="text-primary-600 hover:text-primary-700 uppercase w-full w-56 py-3 px-6 font-semibold cursor-pointer underline"
                                                    onClick={() => {
                                                        navigate("/recommended-job", {
                                                            state: {
                                                                recommendedJobCode: recommendedJob.jobCode,
                                                            },
                                                        });
                                                    }}
                                                >
                                                    {recommendedJob.jobTitle}
                                                </td>
                                            ) : (
                                                <td className="text-blue-gray-600 uppercase w-full w-56 py-3 px-6 font-semibold">
                                                    {recommendedJob.jobTitle}
                                                </td>
                                            )}
                                            <td className="py-4 px-6 text-blue-gray-500">
                                                {recommendedJob.firstJobInterestArea},{" "}
                                                {recommendedJob.secondJobInterestArea}
                                            </td>
                                            <td className="py-4 px-6 text-blue-gray-500">{recommendedJob.jobZone}</td>

                                            <td className="py-4 px-6 text-blue-gray-500">
                                                <div className={"flex flex-col gap-2"}>
                                                    <Button
                                                        icon={
                                                            <ChevronRightIcon
                                                                className={
                                                                    "h-4 w-4 ml-2 transition transition-transform transform " +
                                                                    (showMore && "rotate-90")
                                                                }
                                                            />
                                                        }
                                                        onClick={handleShowMore}
                                                        colour={BUTTON_VARIANT_TYPES.UNCOLOURED}
                                                        text="Click to view"
                                                        extraStyling="shadow-none text-primary-500 p-0 focus:ring-0 focus:transparent hover:text-primary-600"
                                                    />
                                                    {showMore && (
                                                        <BasicMotion>
                                                            <p>{recommendedJob.jobDetails}</p>
                                                        </BasicMotion>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default CareeringAhead;
